import React, { useState } from "react";
import { TextField, Button, Grid, Typography, Box } from "@mui/material";
import Swal from "sweetalert2";
import "../css/Common.css";
import "../css/Contact.css";
import '@fortawesome/fontawesome-free/css/all.min.css';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageSquare,
  Send,
  User,
  Building,
  Shield,
  Headphones,
  FileText,
  Heart,
  Zap,
  Users,
  Star,
  CheckCircle
} from "lucide-react";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("Form submitted!", formData);

    // Show SweetAlert notification
    Swal.fire({
      icon: "success",
      title: "Message Sent Successfully!",
      text: "Thank you for contacting us. We'll get back to you soon!",
      confirmButtonText: "OK",
      confirmButtonColor: "#4A7C59"
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <>
      {/* Contact Banner Section */}
      <section className="udhaar-banner-section">
        <div className="udhaar-banner-overlay"></div>
        <div className="udhaar-banner-pattern"></div>
        
        <div className="udhaar-banner-wrapper">
          <div className="udhaar-banner-content">
            <div className="udhaar-banner-text">
              <h1 className="udhaar-banner-title">
                <span className="udhaar-title-line">Get In</span>
                <span className="udhaar-title-highlight">Touch</span>
                <span className="udhaar-title-line">With Us</span>
              </h1>
              
              <p className="udhaar-banner-subtitle">
                We're Here to Help You Succeed
              </p>
              
              <p className="udhaar-banner-description">
                Have questions about our loan services? Need assistance with your application? Our dedicated team is ready to provide personalized support and expert guidance for all your financial needs.
              </p>
              
              <div className="udhaar-banner-stats">
                <div className="udhaar-stat-item">
                  <span className="udhaar-stat-number">24/7</span>
                  <span className="udhaar-stat-label">Support</span>
                </div>
                <div className="udhaar-stat-divider"></div>
                <div className="udhaar-stat-item">
                  <span className="udhaar-stat-number">&lt; 1hr</span>
                  <span className="udhaar-stat-label">Response</span>
                </div>
                <div className="udhaar-stat-divider"></div>
                <div className="udhaar-stat-item">
                  <span className="udhaar-stat-number">100%</span>
                  <span className="udhaar-stat-label">Secure</span>
                </div>
              </div>
              
              <div className="udhaar-banner-actions">
                <a href="tel:09311437690" className="udhaar-action-btn udhaar-action-primary">
                  <div className="udhaar-btn-icon">
                    <Phone size={20} />
                  </div>
                  <div className="udhaar-btn-content">
                    <span className="udhaar-btn-text">Call Now</span>
                    <span className="udhaar-btn-subtext">09311437690</span>
                  </div>
                </a>
                
                <a href="mailto:contact@udhaarportal.com" className="udhaar-action-btn udhaar-action-secondary">
                  <div className="udhaar-btn-icon">
                    <Mail size={20} />
                  </div>
                  <div className="udhaar-btn-content">
                    <span className="udhaar-btn-text">Send Email</span>
                    <span className="udhaar-btn-subtext">contact@udhaarportal.com</span>
                  </div>
                </a>
              </div>
            </div>
            
            <div className="udhaar-banner-visual">
              <div className="udhaar-visual-card">
                <div className="udhaar-card-header">
                  <div className="udhaar-card-icon">
                    <Headphones size={32} />
                  </div>
                  <h3 className="udhaar-card-title">Expert Support</h3>
                  <p className="udhaar-card-subtitle">Professional assistance for all your financial needs</p>
                </div>
                
                <div className="udhaar-card-features">
                  <div className="udhaar-feature-item">
                    <div className="udhaar-feature-icon">
                      <Phone size={20} />
                    </div>
                    <div className="udhaar-feature-content">
                      <span className="udhaar-feature-title">Direct Call</span>
                      <span className="udhaar-feature-desc">Speak with our experts</span>
                    </div>
                  </div>
                  
                  <div className="udhaar-feature-item">
                    <div className="udhaar-feature-icon">
                      <Mail size={20} />
                    </div>
                    <div className="udhaar-feature-content">
                      <span className="udhaar-feature-title">Email Support</span>
                      <span className="udhaar-feature-desc">Detailed written assistance</span>
                    </div>
                  </div>
                  
                  <div className="udhaar-feature-item">
                    <div className="udhaar-feature-icon">
                      <Clock size={20} />
                    </div>
                    <div className="udhaar-feature-content">
                      <span className="udhaar-feature-title">Quick Response</span>
                      <span className="udhaar-feature-desc">Fast turnaround time</span>
                    </div>
                  </div>
                </div>
                
                <div className="udhaar-card-footer">
                  <div className="udhaar-footer-badge">
                    <CheckCircle size={16} />
                    <span>RBI Compliant & Secure</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="contact-features-section">
        <div className="contact-features-container">
          <div className="contact-features-header">
            <h2 className="contact-features-title">
              Why Choose <span className="contact-features-highlight">Udhaar Portal</span>
            </h2>
            <p className="contact-features-description">
              We're committed to providing fast, secure, and compliant financial solutions with exceptional customer service.
            </p>
          </div>

          <div className="contact-features-grid">
            {/* Fast Processing */}
            <div className="contact-feature-card">
              <div className="contact-feature-card-top"></div>
              <div className="contact-feature-icon">
                <Zap size={24} />
              </div>
              <h3 className="contact-feature-title">
                Fast Processing
              </h3>
              <p className="contact-feature-description">
                Quick loan approvals with our streamlined digital process and advanced automation technology.
              </p>
            </div>

            {/* Secure & Safe */}
            <div className="contact-feature-card">
              <div className="contact-feature-card-top"></div>
              <div className="contact-feature-icon">
                <Shield size={24} />
              </div>
              <h3 className="contact-feature-title">
                Secure & Safe
              </h3>
              <p className="contact-feature-description">
                Bank-level security with RBI compliance and advanced encryption to protect your data and transactions.
              </p>
            </div>

            {/* Customer Support */}
            <div className="contact-feature-card">
              <div className="contact-feature-card-top"></div>
              <div className="contact-feature-icon">
                <Headphones size={24} />
              </div>
              <h3 className="contact-feature-title">
                Customer Support
              </h3>
              <p className="contact-feature-description">
                Dedicated support team available to help you with any questions or concerns throughout your loan journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info-section">
        <div className="contact-info-bg"></div>
        
        <div className="contact-info-container">
          <div className="contact-info-header">
            <h2 className="contact-info-title">
              Get In Touch
            </h2>
            <p className="contact-info-description">
              Choose your preferred way to reach us. Our team is ready to assist you with any questions or concerns.
            </p>
          </div>

          <div className="contact-info-grid">
            {/* Phone Contact */}
            <div className="contact-info-card">
              <div className="contact-info-card-top"></div>
              <div className="contact-info-icon">
                <Phone size={24} />
              </div>
              <h3 className="contact-info-card-title">
                Call Us
              </h3>
              <p className="contact-info-card-description">
                Speak directly with our customer support team
              </p>
              <a href="tel:8802366666" className="contact-info-link">
                8802366666
              </a>
            </div>

            {/* Email Contact */}
            <div className="contact-info-card">
              <div className="contact-info-card-top"></div>
              <div className="contact-info-icon">
                <Mail size={24} />
              </div>
              <h3 className="contact-info-card-title">
                Email Us
              </h3>
              <p className="contact-info-card-description">
                Send us an email for detailed inquiries
              </p>
              <a href="mailto:contact@udhaarportal.com" className="contact-info-link">
                contact@udhaarportal.com
              </a>
            </div>

            {/* Address */}
            <div className="contact-info-card">
              <div className="contact-info-card-top"></div>
              <div className="contact-info-icon">
                <MapPin size={24} />
              </div>
              <h3 className="contact-info-card-title">
                Visit Us
              </h3>
              <p className="contact-info-card-description">
                Our registered office address
              </p>
              <p className="contact-info-address">
                15 Sant Nagar, East of Kailash, New Delhi - 110065
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support & Grievance Section */}
      <section className="contact-support-section">
        <div className="contact-support-bg"></div>
        
        <div className="contact-support-container">
          <div className="contact-support-header">
            <h2 className="contact-support-title">
              Support & <span className="contact-support-highlight">Grievance</span>
            </h2>
            <p className="contact-support-description">
              We're committed to providing excellent customer support and addressing any concerns promptly. 
              Choose the appropriate contact channel based on your needs.
            </p>
          </div>

          <div className="contact-support-grid">
            {/* Customer Support */}
            <div className="contact-support-card">
              <div className="contact-support-card-top"></div>
              <div className="contact-support-icon">
                <MessageSquare size={24} />
              </div>
              <h3 className="contact-support-card-title">
                Customer Support
              </h3>
              <p className="contact-support-card-description">
                For general inquiries, technical support, and assistance with your loan application
              </p>
              <a href="mailto:contact@udhaarportal.com" className="contact-support-link">
                contact@udhaarportal.com
              </a>
            </div>

            {/* Grievance Redressal Officer */}
            <div className="contact-support-card">
              <div className="contact-support-card-top"></div>
              <div className="contact-support-icon">
                <Building size={24} />
              </div>
              <h3 className="contact-support-card-title">
                Grievance Redressal Officer (GRO)
              </h3>
              <p className="contact-support-card-description">
                <strong>Mr Rohan Bagga</strong>
              </p>
              <p className="contact-support-card-description">
                15 Sant Nagar, East of Kailash, New Delhi - 110065
              </p>
              <p className="contact-support-card-description">
                <strong>Contact:</strong> 9311528815
              </p>
              <p className="contact-support-card-description">
                <strong>Timing:</strong> 10:00 - 19:00, Mon-Sat (except holidays)
              </p>
              <a href="mailto:contact@udhaarportal.com" className="contact-support-link">
                contact@udhaarportal.com
              </a>
            </div>

            {/* Nodal Officer */}
            <div className="contact-support-card">
              <div className="contact-support-card-top"></div>
              <div className="contact-support-icon">
                <User size={24} />
              </div>
              <h3 className="contact-support-card-title">
                Nodal Officer
              </h3>
              <p className="contact-support-card-description">
                <strong>Mr Kulmehar Singh</strong>
              </p>
              <p className="contact-support-card-description">
                15 Sant Nagar, East of Kailash, New Delhi - 110065
              </p>
              <p className="contact-support-card-description">
                <strong>Contact:</strong> 8802366666
              </p>
              <p className="contact-support-card-description">
                <strong>Timing:</strong> 10:00 - 19:00, Mon-Sat (except holidays)
              </p>
              <a href="mailto:contact@udhaarportal.com" className="contact-support-link">
                contact@udhaarportal.com
              </a>
            </div>
          </div>

          {/* Response Time Commitment */}
          <div className="contact-response-time">
            <div className="contact-response-time-top"></div>
            <h4 className="contact-response-time-title">
              Response Time Commitment
            </h4>
            <p className="contact-response-time-description">
              We strive to respond to all inquiries within 24 hours during business days. 
              For urgent matters, please call our customer support number for immediate assistance.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="contact-form-container">
          <div className="contact-form-header">
            <h2 className="contact-form-title">
              Submit a <span className="contact-form-highlight">Complaint</span>
            </h2>
            <p className="contact-form-description">
              We take all complaints seriously and are committed to resolving them promptly. 
              Please provide detailed information about your complaint below.
            </p>
          </div>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  variant="outlined"
                  fullWidth
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Subject"
                  variant="outlined"
                  fullWidth
                  required
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Message"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                className="contact-form-submit-btn"
              > 
                Submit Complaint
              </Button>
            </Box>
          </Box>
        </div>
      </section>
    </>
  );
};

export default ContactUs;